import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Globe2, ShieldCheck, Users } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const lineAnim = (delay) => ({
  initial: { y: "115%" },
  animate: { y: 0 },
  transition: { duration: 1, delay, ease: [0.16, 1, 0.3, 1] },
});

const TRUST = [
  { icon: ShieldCheck, label: "Confidencialidad garantizada" },
  { icon: Users, label: "Trato de socio a socio" },
  { icon: Globe2, label: "Alcance internacional" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yGhost = useTransform(scrollYProgress, [0, 1], [0, -140]);

  return (
    <section ref={ref} data-testid="hero" className="relative flex min-h-screen items-center overflow-hidden pt-28 pb-20">
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(900px 500px at 85% 10%, rgba(192,160,98,0.10), transparent 60%), radial-gradient(700px 500px at 5% 90%, rgba(26,37,66,0.9), transparent 65%)",
        }}
      />
      <motion.span
        aria-hidden
        style={{ y: yGhost, WebkitTextStroke: "1px rgba(192,160,98,0.16)" }}
        className="pointer-events-none absolute -right-10 top-6 select-none font-serif italic leading-none text-transparent text-[16rem] md:text-[26rem]"
      >
        &
      </motion.span>

      <div className="relative mx-auto w-full max-w-7xl px-6 md:px-10">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.15 }} className="eyebrow">
          Privado y confidencial
        </motion.p>

        <h1 className="mt-8 max-w-5xl font-serif text-6xl leading-[1.04] text-white sm:text-7xl lg:text-8xl">
          <span className="block overflow-hidden pb-1">
            <motion.span className="block" {...lineAnim(0.25)}>
              Vender tu empresa
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-1">
            <motion.span className="block" {...lineAnim(0.4)}>
              es un gran paso.
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-2">
            <motion.span className="block italic text-gold" {...lineAnim(0.55)}>
              Te ayudamos a darlo.
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85, ease: [0.16, 1, 0.3, 1] }}
          className="mt-9 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg"
        >
          Somos la boutique de <strong className="font-semibold text-white">M&amp;A y consultoría financiera</strong>{" "}
          que acompaña a empresarios e inversores en la venta de empresas: valoración, búsqueda de compradores,
          negociación y cierre, con absoluta confidencialidad.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#contacto"
            data-testid="hero-cta-primary"
            onClick={(e) => {
              e.preventDefault();
              scrollToId("#contacto");
            }}
            className="bg-gold px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-ink transition-all duration-300 hover:bg-gold-light"
          >
            Consulta confidencial
          </a>
          <a
            href="#servicios"
            data-testid="hero-cta-secondary"
            onClick={(e) => {
              e.preventDefault();
              scrollToId("#servicios");
            }}
            className="group flex items-center gap-3 px-2 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-slate-200 transition-colors hover:text-gold"
          >
            Nuestros servicios
            <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
          </a>
        </motion.div>

        <motion.ul
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
          className="mt-14 flex flex-wrap gap-x-10 gap-y-4"
        >
          {TRUST.map(({ icon: Icon, label }) => (
            <li key={label} className="flex items-center gap-3 text-[13px] font-medium tracking-wide text-slate-300">
              <Icon className="h-4 w-4 text-gold" />
              {label}
            </li>
          ))}
        </motion.ul>

      </div>
    </section>
  );
}
